import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from "components/GlobalContext";
import { Card, Col, Form, Row } from "react-bootstrap";
import IElt, { IEltCreateOrUpdate, IEltInitResult } from "models/sql/IElt";
import EltService from "services/EltService";
import moment from "moment";
import { useBeforeUnload } from "utilities/UIUtilities";
import DetailFooter from "components/DetailFooter";
import { generateLog, getBaseDomain } from "utilities/MiscUtilities";
import CountryChooser from "components/CountryChooser";
import DPSEntitySelector from "components/DPSEntitySelector";
import IFireDepartment, { IFireDepartmentFilters } from "models/sql/IFireDepartment";
import FireDepartmentService from "services/FireDepartmentService";
import IFireStation, { IFireStationFilters } from "models/sql/IFireStation";
import FireStationService from "services/FireStationService";
import { ReactQrCode } from '@devmehq/react-qr-code';
import { copyTextToClipboardAsync, printElement } from "utilities/BrowserUtility";

interface IEltsDetailProps {
    baseData?: IEltInitResult;
}

const EltsDetail = (props: IEltsDetailProps) => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const { id }: any = useParams();
    const [elt, setElt] = useState<IElt | null>({
        eltId: -1,
        code: crypto.randomUUID(),
        created: (new Date()).toISOString(),
        lastUpdated: undefined,
        lastUpdatedBy: undefined,
        fireDepartmentId: undefined,
        fireStationId: undefined,
        redirects: 0,
        type: "",
        systemDataJson: "",
        postalCode: "",
        country: "",
        targetOverride: "",
        manualFireDepartment: "",
        manualFireStation: "",
        placement: ""
    });
    const [fireDepartment, setFireDepartment] = useState<IFireDepartment>();
    const [fireStation, setFireStation] = useState<IFireStation>();
    const navigate = useNavigate();
    const [hasFormDataChanged, setHasFormDataChanged] = useState(false);
    const types = ["hra"];

    useBeforeUnload(
        hasFormDataChanged,
        "You have unsaved changes, please save them before leaving the page.");

    useEffect(
        () => {
            (async () => {
                if (id !== undefined) {
                    globalContext.setLoading(true);

                    const accessToken = await globalContext.getAccessTokenAsync();
                    const result = await EltService.GetAsync(accessToken, id);

                    if (result.record) {
                        setElt(result.record);

                        if (result.record.fireDepartmentId !== null && result.record.fireDepartmentId !== undefined) {
                            const departmentResult = await FireDepartmentService.GetAsync(accessToken, result.record.fireDepartmentId);

                            if (departmentResult.record) {
                                setFireDepartment(departmentResult.record);
                            }
                        }

                        if (result.record.fireStationId !== null && result.record.fireStationId !== undefined) {
                            const fireStationResult = await FireStationService.GetAsync(accessToken, result.record.fireStationId);

                            if (fireStationResult.record) {
                                setFireStation(fireStationResult.record);
                            }
                        }
                    }
                    else {
                        setElt(null);
                        globalContext.showErrorMessage("Unable to load Elt");
                    }

                    globalContext.setLoading(false);
                }
            })();
        }, [id]);

    const deleteAsync = async () => {
        if (elt && elt.eltId && window.confirm("Are you SURE you want to delete this?")) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();
            await EltService.DeleteAsync(accessToken, elt.eltId);
            globalContext.setLoading(false);
            navigate("/Elts");
        }
    };

    const submitFormAsync = async () => {
        if (elt) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();

            const data: IEltCreateOrUpdate = {
                eltId: elt.eltId,
                code: elt.code,
                created: elt.created,
                fireDepartmentId: elt.fireDepartmentId,
                fireStationId: elt.fireStationId,
                lastUpdated: elt.lastUpdated,
                lastUpdatedBy: elt.lastUpdatedBy,
                redirects: elt.redirects,
                type: elt.type,
                systemDataJson: elt.systemDataJson,
                postalCode: elt.postalCode,
                country: elt.country,
                targetOverride: elt.targetOverride,
                manualFireDepartment: elt.manualFireDepartment,
                manualFireStation: elt.manualFireStation,
                placement: elt.placement
            };

            await EltService.CreateOrUpdateAsync(accessToken, data);
            globalContext.setLoading(false);
            setHasFormDataChanged(false);

            if (data.eltId === -1) {
                navigate(-1);
            }
        }
    };

    if (elt) {
        return (
            <>
                <Row>
                    <Col>
                        <Card className="mb-2 mt-2">
                            <Card.Body>
                                <Card.Title>
                                    {
                                        id !== undefined &&
                                        <strong>{elt.code}</strong>
                                    }
                                    {
                                        id === undefined &&
                                        <strong>New Elt</strong>
                                    }
                                    <br />
                                    <button
                                        className="btn btn-primary mt-2" onClick={(e) => {
                                            copyTextToClipboardAsync("https://" + getBaseDomain() + "/Elt/" + elt.code)
                                                .then(
                                                    (result) => {
                                                        if (result) {
                                                            window.alert("Copied!");
                                                        }
                                                        else {
                                                            console.log("didn't work");
                                                        }
                                                    });
                                        }}
                                    >Copy ELT Redirect Url</button>
                                    <button
                                        className="btn btn-primary mt-2 ms-2" onClick={(e) => {
                                            const el = document.getElementById(`qr`);
                                            if (el) {
                                                el.style.display = "";
                                                printElement(el);
                                                el.style.display = "none";
                                            }
                                        }}
                                    >Print QR Code</button>
                                </Card.Title >

                                <DPSEntitySelector<IFireDepartment, IFireDepartmentFilters>
                                    entityFilterSearchPropName={"fireDeptNames"}
                                    entityService={FireDepartmentService}
                                    entityTypeName="Department"
                                    defaultEntityFilter={{}}
                                    selectedEntityRef={fireDepartment}
                                    generateEntityTitle={(entry) => `${entry.fireDeptName} ${entry.hqcity} ${entry.hqstate}, ${entry.hqzip} ${entry.hqCountry}`}
                                    onClickExternalHandler={(entry) => {
                                        if (entry) {
                                            window.open(`/DPS/Departments/Detail/${entry.fireDepartmentId}`);
                                        }
                                    }}
                                    onClickAddNewHandler={() => {
                                        window.open(`/DPS/Departments/Detail/?defs={"fireDeptName":"${elt.manualFireDepartment ?? ""}","country":"${elt.country ?? ""}"}`);
                                    }}
                                    entityChangeHandler={(newEntry) => {
                                        setElt({ ...elt, fireDepartmentId: newEntry?.fireDepartmentId || undefined });
                                        setFireDepartment(newEntry);
                                        setHasFormDataChanged(true);
                                    }}
                                />

                                <DPSEntitySelector<IFireStation, IFireStationFilters>
                                    entityFilterSearchPropName={"names"}
                                    entityService={FireStationService}
                                    entityTypeName="Station"
                                    defaultEntityFilter={{ departmentIds: fireDepartment ? [fireDepartment.fireDepartmentId] : undefined }}
                                    selectedEntityRef={fireStation}
                                    generateEntityTitle={(entry) => `${entry.name} ${entry.city} ${entry.state}, ${entry.postalCode} ${entry.country}`}
                                    onClickExternalHandler={(entry) => {
                                        if (entry) {
                                            window.open(`/DPS/Station/Detail/${entry.fireStationId}`);
                                        }
                                    }}
                                    onClickAddNewHandler={() => {
                                        window.open(`/DPS/Station/Detail/?defs={"fireDepartmentId":"${elt.fireDepartmentId ? elt.fireDepartmentId : ""}","country":"${elt.country ?? ""}"}`);
                                    }}
                                    entityChangeHandler={(newEntry) => {
                                        setElt({ ...elt, fireStationId: newEntry?.fireStationId || undefined });
                                        setFireStation(newEntry);
                                        setHasFormDataChanged(true);
                                    }}
                                />

                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Code</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setElt({ ...elt, code: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={elt.code}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Type</Form.Label>
                                            <Form.Select
                                                onChange={async (e) => {
                                                    setElt({ ...elt, type: e.target.value });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={elt.type}>
                                                <option key={"empty"} value={""}>-</option>
                                                {
                                                    types.map((s, index) => <option key={s} value={s}>{s}</option>)
                                                }
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Created Date</Form.Label>
                                            {
                                                <Form.Control
                                                    type="date"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            if (e.target.value) {
                                                                setElt({ ...elt, created: e.target.value });
                                                                setHasFormDataChanged(true);
                                                            }
                                                        }
                                                    }
                                                    value={elt.created ? moment(elt.created).format('yyyy-MM-DD') : undefined}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Uses</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setElt({ ...elt, redirects: parseInt(e.target.value) });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={elt.redirects}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Postal Code</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setElt({ ...elt, postalCode: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={elt.postalCode}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Country</Form.Label>
                                            <CountryChooser
                                                defaultValue={elt.country}
                                                onChange={(data: string) => {
                                                    setElt({ ...elt, country: data });
                                                    setHasFormDataChanged(true);
                                                }} />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Manual Department Name</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setElt({ ...elt, manualFireDepartment: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={elt.manualFireDepartment}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Manual Station Name</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setElt({ ...elt, manualFireStation: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={elt.manualFireStation}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Placement Location</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setElt({ ...elt, placement: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={elt.placement}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>
                                                Target Override<br />
                                                <i>(Ignores all other settings and forwards to this url directly.)</i>
                                            </Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setElt({ ...elt, targetOverride: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={elt.targetOverride}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {
                                    globalContext.roles.some(r => r === "Web.Admin") &&
                                    <Row>
                                        <Col>
                                            <Form.Group className="mb-3" >
                                                <Form.Label>Last Updated</Form.Label>
                                                {
                                                    <Form.Control
                                                        type="date"
                                                        readOnly
                                                        value={elt.lastUpdated ? moment(elt.lastUpdated).format('yyyy-MM-DD') : undefined}
                                                    />
                                                }
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group className="mb-3" >
                                                <Form.Label>Last Updated By</Form.Label>
                                                {
                                                    <Form.Control
                                                        type="text"
                                                        readOnly
                                                        value={elt.lastUpdatedBy}
                                                    />
                                                }
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                }
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                {
                    globalContext.roles.some(r => r === "Web.Admin") &&
                    <Row className="align-items-end">
                        <Col>
                            <Card className="mb-2">
                                <Card.Body>
                                    <Card.Title>
                                        <strong>Log</strong><br />
                                    </Card.Title>
                                    {generateLog<IElt>(elt)}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                }

                {
                    globalContext.roles.some(r => r === "Web.Admin") &&
                    <DetailFooter
                        hasFormDataChanged={hasFormDataChanged}
                        deleteAsync={deleteAsync}
                        submitFormAsync={submitFormAsync}
                    />
                }

                <span
                    className="print-qr-code"
                    id={`qr`}
                    style={{ display: "none" }} >
                    <ReactQrCode value={"https://" + getBaseDomain() + "/ELT/" + elt.code} />
                </span>

            </>
        );
    }
    else {
        return (
            <>
                <h1>There was an issue retreiving the Elt, please try again.</h1>
            </>
        );
    }
}

export default EltsDetail;
